/**
 * ReportEmptyState Component
 * Empty state and error states for report components
 *
 * Story 1.6: Report Loading States & Error Handling
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  BarChart3,
  FileX,
  AlertTriangle,
  Filter,
  Calendar,
  Target,
} from 'lucide-react';
import { Card, Button } from '../common';

export interface ReportEmptyStateProps {
  /** Type of empty state to display */
  type?: 'no-data' | 'no-results' | 'error' | 'filtered';
  /** Title text */
  title?: string;
  /** Description text */
  description?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Action button configuration */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Secondary action button */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Additional class names */
  className?: string;
}

/**
 * Empty state component for reports
 */
export const ReportEmptyState: React.FC<ReportEmptyStateProps> = ({
  type = 'no-data',
  title,
  description,
  icon,
  action,
  secondaryAction,
  className,
}) => {
  // Default content based on type
  const defaults = getDefaultContent(type);
  const displayTitle = title || defaults.title;
  const displayDescription = description || defaults.description;
  const displayIcon = icon || defaults.icon;

  return (
    <Card className={clsx('p-12', className)}>
      <div className="text-center max-w-md mx-auto">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-6">
          {displayIcon}
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-stone-900 mb-2">
          {displayTitle}
        </h3>

        {/* Description */}
        <p className="text-stone-500 mb-6">
          {displayDescription}
        </p>

        {/* Actions */}
        {(action || secondaryAction) && (
          <div className="flex items-center justify-center gap-3">
            {secondaryAction && (
              <Button
                variant="secondary"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
            {action && (
              <Button
                variant="primary"
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * Get default content based on empty state type
 */
function getDefaultContent(type: string): {
  title: string;
  description: string;
  icon: React.ReactNode;
} {
  switch (type) {
    case 'no-data':
      return {
        title: 'No Data Available',
        description: 'There isn\'t enough data to generate this report yet. Create some OKRs to get started.',
        icon: <BarChart3 className="w-8 h-8 text-stone-400" />,
      };
    case 'no-results':
      return {
        title: 'No Results Found',
        description: 'No data matches your current selection. Try adjusting your filters or date range.',
        icon: <FileX className="w-8 h-8 text-stone-400" />,
      };
    case 'filtered':
      return {
        title: 'No Matching Data',
        description: 'Your filters didn\'t match any data. Try removing some filters to see more results.',
        icon: <Filter className="w-8 h-8 text-stone-400" />,
      };
    case 'error':
      return {
        title: 'Something Went Wrong',
        description: 'We couldn\'t load this report. Please try again or contact support if the problem persists.',
        icon: <AlertTriangle className="w-8 h-8 text-red-400" />,
      };
    default:
      return {
        title: 'No Data',
        description: 'No data is available for this report.',
        icon: <BarChart3 className="w-8 h-8 text-stone-400" />,
      };
  }
}

/**
 * Report error state with retry
 */
export interface ReportErrorStateProps {
  error?: Error | string;
  onRetry?: () => void;
  className?: string;
}

export const ReportErrorState: React.FC<ReportErrorStateProps> = ({
  error,
  onRetry,
  className,
}) => {
  const errorMessage = error instanceof Error ? error.message : error;

  return (
    <ReportEmptyState
      type="error"
      title="Failed to Load Report"
      description={errorMessage || 'An unexpected error occurred while loading this report.'}
      icon={<AlertTriangle className="w-8 h-8 text-red-400" />}
      action={
        onRetry
          ? {
              label: 'Try Again',
              onClick: onRetry,
            }
          : undefined
      }
      className={className}
    />
  );
};

/**
 * No OKRs in period state
 */
export const NoOKRsInPeriodState: React.FC<{
  onChangeDateRange?: () => void;
  className?: string;
}> = ({ onChangeDateRange, className }) => (
  <ReportEmptyState
    title="No OKRs in This Period"
    description="There are no OKRs created during the selected date range. Try selecting a different time period."
    icon={<Calendar className="w-8 h-8 text-stone-400" />}
    action={
      onChangeDateRange
        ? {
            label: 'Change Date Range',
            onClick: onChangeDateRange,
          }
        : undefined
    }
    className={className}
  />
);

/**
 * No OKRs created yet state
 */
export const NoOKRsCreatedState: React.FC<{
  onCreateOKR?: () => void;
  className?: string;
}> = ({ onCreateOKR, className }) => (
  <ReportEmptyState
    title="Get Started with OKRs"
    description="Create your first Objective and Key Results to start tracking progress and viewing reports."
    icon={<Target className="w-8 h-8 text-stone-400" />}
    action={
      onCreateOKR
        ? {
            label: 'Create First OKR',
            onClick: onCreateOKR,
          }
        : undefined
    }
    className={className}
  />
);

/**
 * Filtered results empty state
 */
export const FilteredEmptyState: React.FC<{
  onClearFilters?: () => void;
  className?: string;
}> = ({ onClearFilters, className }) => (
  <ReportEmptyState
    type="filtered"
    action={
      onClearFilters
        ? {
            label: 'Clear Filters',
            onClick: onClearFilters,
          }
        : undefined
    }
    className={className}
  />
);

export default ReportEmptyState;
